import { useEffect, useMemo, useState } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Loader2, Calendar, Clock, MapPin, Users, Tag, ArrowLeft, RefreshCw, Ticket, ExternalLink } from "lucide-react";
import CheckoutModal from "@/components/CheckoutModal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AUDIENCE_LABEL = {
  everyone: "Everyone",
  couples: "Couples",
  tv: "TV",
  single_guys: "Single Guys",
  single_ladies: "Single Ladies",
};

const NTH = { 1: "1st", 2: "2nd", 3: "3rd", 4: "4th", 5: "5th" };
const DAY_NAMES = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const jsDayToOurDay = (jsDay) => (jsDay + 6) % 7;

const fmtDate = (d) => {
  try {
    return new Date(d + "T00:00:00").toLocaleDateString("en-GB", {
      weekday: "long",
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  } catch {
    return d;
  }
};

const fmtGBP = (n) =>
  new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP" }).format(n);

export default function EventPublic() {
  const { slug: venueSlug, eventSlug } = useParams();
  const [searchParams] = useSearchParams();
  const dateParam = searchParams.get("date") || "";
  const [data, setData] = useState(null);
  const [status, setStatus] = useState("loading");
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [whos, setWhos] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const url = dateParam
          ? `${API}/venues/${venueSlug}/events/${eventSlug}?date=${encodeURIComponent(dateParam)}`
          : `${API}/venues/${venueSlug}/events/${eventSlug}`;
        const { data: res } = await axios.get(url);
        setData(res);
        setStatus("ok");
      } catch {
        setStatus("notfound");
      }
      try {
        const { data: w } = await axios.get(
          `${API}/venues/${venueSlug}/events/${eventSlug}/whos-going`
        );
        setWhos(w);
      } catch {
        setWhos(null);
      }
    })();
  }, [venueSlug, eventSlug, dateParam]);

  const recurrence = useMemo(() => {
    if (!data) return null;
    const ev = data.event;
    if (ev.recurrence === "none") return null;
    const day = DAY_NAMES[jsDayToOurDay(new Date(ev.first_date + "T00:00:00").getDay())];
    if (ev.recurrence === "every_week") return `Every ${day}`;
    if (ev.recurrence === "monthly_nth" && ev.recurrence_nth) return `Every ${NTH[ev.recurrence_nth]} ${day} of the month`;
    return null;
  }, [data]);

  const onSaleLine = useMemo(() => {
    if (!data) return "";
    const ev = data.event;
    if (ev.on_sale === "always") return "Tickets on sale now";
    if (ev.on_sale === "specific_date" && ev.on_sale_date) return `Tickets on sale from ${fmtDate(ev.on_sale_date)}`;
    const map = { "7_days": 7, "14_days": 14, "21_days": 21 };
    const days = map[ev.on_sale];
    if (!days) return "";
    const eventDate = new Date(ev.first_date + "T00:00:00");
    const saleDate = new Date(eventDate);
    saleDate.setDate(saleDate.getDate() - days);
    return `Tickets on sale from ${saleDate.toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}`;
  }, [data]);

  if (status === "loading") {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <Loader2 size={20} className="animate-spin text-[#D9A6F5]" />
      </div>
    );
  }
  if (status === "notfound") {
    return (
      <div className="min-h-screen bg-black text-[#F5F5F0] flex flex-col items-center justify-center px-6">
        <div className="text-xs uppercase tracking-[0.3em] text-[#D9A6F5] mb-4">404</div>
        <h1 className="text-4xl md:text-5xl font-black tracking-tighter mb-4">Event not found</h1>
        <p className="text-[#A3A3A3] mb-6">This event doesn't exist or is no longer live.</p>
        <Link to="/" className="text-[#D9A6F5] hover:text-[#E8C8FF] uppercase tracking-[0.22em] text-xs">
          Back to home
        </Link>
      </div>
    );
  }

  const { venue, event } = data;

  return (
    <div data-testid="event-public-page" className="min-h-screen bg-black text-[#F5F5F0] grain">
      {/* HERO */}
      <section className="relative overflow-hidden border-b border-white/10">
        {event.photo_data_uri ? (
          <div className="absolute inset-0">
            <img src={event.photo_data_uri} alt="" className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/75 to-black" />
          </div>
        ) : (
          <div className="absolute inset-0 bg-gradient-to-b from-[#0A0A0A] to-black" />
        )}
        <div className="relative z-[2] max-w-5xl mx-auto px-6 md:px-12 py-24 md:py-36">
          <Link
            to={`/v/${venue.slug}`}
            className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-[#F5F5F0]/80 hover:text-[#D9A6F5] mb-8 transition-colors"
          >
            <ArrowLeft size={14} /> {venue.name}
          </Link>
          {event.theme && (
            <div className="text-xs uppercase tracking-[0.3em] text-[#D9A6F5] mb-4">{event.theme}</div>
          )}
          <h1
            data-testid="event-public-name"
            className="font-black tracking-[-0.03em] leading-[0.95]"
            style={{ fontSize: "clamp(2.5rem, 8vw, 6rem)" }}
          >
            {event.name}
          </h1>
          <div className="mt-8 flex flex-wrap gap-x-8 gap-y-3 text-lg">
            <span className="inline-flex items-center gap-2">
              <Calendar size={16} className="text-[#D9A6F5]" /> {fmtDate(event.first_date)}
            </span>
            <span className="inline-flex items-center gap-2">
              <Clock size={16} className="text-[#D9A6F5]" /> {event.time}
            </span>
            {recurrence && (
              <span className="inline-flex items-center gap-2 text-[#A3A3A3]">
                <RefreshCw size={14} className="text-[#D9A6F5]" /> {recurrence}
              </span>
            )}
          </div>
        </div>
      </section>

      <div className="max-w-5xl mx-auto px-6 md:px-12 py-16 md:py-24 grid grid-cols-1 lg:grid-cols-3 gap-10">
        {/* LEFT */}
        <div className="lg:col-span-2 space-y-10">
          {/* Open to */}
          <div>
            <div className="flex items-center gap-2 text-xs uppercase tracking-[0.28em] text-[#D9A6F5] mb-4">
              <Users size={14} /> Open to
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {event.audiences.map((a) => (
                <span
                  key={a}
                  className="inline-block px-4 py-2 border border-[#D9A6F5]/40 text-sm uppercase tracking-[0.18em] text-[#F5F5F0]"
                >
                  {AUDIENCE_LABEL[a] || a}
                </span>
              ))}
              {typeof event.tickets_sold === "number" && event.tickets_sold > 0 && (
                <span
                  data-testid="event-public-tickets-sold"
                  className="inline-flex items-center gap-1.5 px-4 py-2 border border-white/15 bg-white/5 text-sm uppercase tracking-[0.18em] text-[#F5F5F0]"
                >
                  <Ticket size={12} /> {event.tickets_sold.toLocaleString("en-GB")} sold
                </span>
              )}
            </div>
          </div>

          {/* Pricing */}
          <div>
            <div className="flex items-center gap-2 text-xs uppercase tracking-[0.28em] text-[#D9A6F5] mb-4">
              <Tag size={14} /> Pricing
            </div>
            <div className="bg-[#0A0A0A] border border-white/10 divide-y divide-white/10">
              {event.audiences.map((a) => (
                <div
                  key={a}
                  className="flex items-center justify-between px-5 py-4"
                  data-testid={`event-public-price-${a}`}
                >
                  <div className="text-sm md:text-base font-semibold text-[#F5F5F0]">
                    {AUDIENCE_LABEL[a] || a}
                  </div>
                  <div className="text-lg md:text-xl font-black tracking-tighter text-[#F5F5F0]">
                    {fmtGBP(Number(event.pricing?.[a] ?? 0))}
                  </div>
                </div>
              ))}
            </div>
            {onSaleLine && (
              <p className="mt-4 text-xs uppercase tracking-[0.22em] text-[#A3A3A3]">{onSaleLine}</p>
            )}

            <button
              type="button"
              onClick={() => setCheckoutOpen(true)}
              data-testid="event-get-tickets"
              className="mt-6 w-full md:w-auto inline-flex items-center justify-center gap-2 bg-[#D9A6F5] text-black hover:bg-[#E8C8FF] px-8 py-4 text-xs uppercase tracking-[0.24em] font-bold transition-colors"
            >
              <Ticket size={14} /> Get Tickets
            </button>
          </div>
        </div>

        {/* RIGHT — venue card */}
        <aside className="space-y-6">
          <div className="bg-[#0A0A0A] border border-white/10 p-6">
            <div className="flex items-center gap-2 text-xs uppercase tracking-[0.24em] text-[#D9A6F5] mb-4">
              <MapPin size={14} /> Venue
            </div>
            <div className="flex items-center gap-4">
              {venue.logo_data_uri && (
                <div className="h-14 w-14 shrink-0 bg-black border border-white/10 flex items-center justify-center p-1.5 overflow-hidden">
                  <img src={venue.logo_data_uri} alt={venue.name} className="max-h-full max-w-full object-contain" />
                </div>
              )}
              <div className="min-w-0">
                <div className="text-base font-bold text-[#F5F5F0] truncate">{venue.name}</div>
                <div className="text-xs text-[#A3A3A3] truncate">
                  {venue.address?.city}
                  {venue.address?.postcode ? `, ${venue.address.postcode}` : ""}
                </div>
              </div>
            </div>
            <Link
              to={`/v/${venue.slug}`}
              className="mt-5 inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-[#D9A6F5] hover:text-[#E8C8FF]"
            >
              View venue page →
            </Link>
          </div>

          {/* Who's Going */}
          <WhosGoingCard whos={whos} />

          {/* Upcoming dates (recurring events) */}
          {Array.isArray(data.occurrences) && data.occurrences.length > 1 && (
            <UpcomingDatesCard
              occurrences={data.occurrences}
              activeDate={dateParam || event.first_date}
              venueSlug={venue.slug}
              eventSlug={event.slug}
              baseTheme={event.theme}
            />
          )}
        </aside>
      </div>

      <footer className="border-t border-white/10">
        <div className="max-w-5xl mx-auto px-6 md:px-12 py-8 text-center text-xs uppercase tracking-[0.22em] text-[#A3A3A3]">
          Powered by{" "}
          <Link to="/" className="text-[#D9A6F5] hover:text-[#E8C8FF]">Swingle</Link>
        </div>
      </footer>

      <CheckoutModal
        open={checkoutOpen}
        onClose={() => setCheckoutOpen(false)}
        venue={venue}
        event={event}
      />
    </div>
  );
}

function WhosGoingCard({ whos }) {
  const groups = whos?.groups || [];
  return (
    <div
      className="bg-[#0A0A0A] border border-white/10 p-6"
      data-testid="whos-going-card"
    >
      <div className="mb-4 inline-flex items-center gap-2 text-xs uppercase tracking-[0.24em] text-[#D9A6F5]">
        <Users size={14} /> Who&apos;s going
      </div>

      {groups.length === 0 ? (
        <p className="text-xs text-[#A3A3A3] leading-relaxed">
          Be the first to opt in — tick the box at checkout to appear here.
        </p>
      ) : (
        <div className="space-y-6">
          {groups.map((g) => (
            <TierGroup key={g.tier} group={g} />
          ))}
        </div>
      )}
    </div>
  );
}

function TierGroup({ group }) {
  const fs = group.fabswingers || [];
  const sh = group.swinghub || [];
  return (
    <div data-testid={`whos-going-group-${group.tier}`}>
      <div className="text-[11px] uppercase tracking-[0.24em] text-[#F5F5F0] font-bold mb-3 pb-2 border-b border-white/10">
        {AUDIENCE_LABEL_PUB[group.tier] || group.tier}
      </div>
      <div className="space-y-4">
        <WhosGoingList
          title="FabSwingers"
          testId={`whos-going-fs-${group.tier}`}
          entries={fs.map((e) => ({ label: e.username, url: e.url }))}
        />
        <WhosGoingList
          title="SwingHub"
          testId={`whos-going-sh-${group.tier}`}
          entries={sh.map((e) => ({
            label: prettySwingHub(e.url),
            url: e.url,
          }))}
        />
      </div>
    </div>
  );
}

const AUDIENCE_LABEL_PUB = {
  everyone: "Everyone",
  couples: "Couples",
  tv: "TV",
  single_guys: "Single Guys",
  single_ladies: "Single Ladies",
};

function WhosGoingList({ title, entries, testId }) {
  if (entries.length === 0) return null;
  return (
    <div data-testid={testId}>
      <div className="text-[10px] uppercase tracking-[0.24em] text-[#A3A3A3] mb-2">{title}</div>
      <ul className="space-y-1.5">
        {entries.map((e, i) => (
          <li key={`${testId}-${i}`}>
            <a
              href={e.url}
              target="_blank"
              rel="noopener noreferrer"
              className="group inline-flex items-center gap-2 text-sm text-[#F5F5F0] hover:text-[#D9A6F5] transition-colors break-all"
            >
              <span className="truncate">{e.label}</span>
              <ExternalLink
                size={11}
                className="text-[#A3A3A3] group-hover:text-[#D9A6F5] shrink-0"
              />
            </a>
          </li>
        ))}
      </ul>
    </div>
  );
}

function prettySwingHub(url) {
  try {
    const u = new URL(url);
    const path = u.pathname.replace(/^\//, "").replace(/\/$/, "");
    return path || u.host;
  } catch {
    return url;
  }
}

function UpcomingDatesCard({ occurrences, activeDate, venueSlug, eventSlug, baseTheme }) {
  return (
    <div
      className="bg-[#0A0A0A] border border-white/10 p-6"
      data-testid="upcoming-dates-card"
    >
      <div className="mb-4 inline-flex items-center gap-2 text-xs uppercase tracking-[0.24em] text-[#D9A6F5]">
        <Calendar size={14} /> Upcoming dates
      </div>
      <ul className="space-y-2">
        {occurrences.map((occ) => {
          const isActive = occ.date === activeDate;
          const displayTheme = occ.theme || baseTheme || "";
          const label = new Date(occ.date + "T00:00:00").toLocaleDateString("en-GB", {
            weekday: "short",
            day: "numeric",
            month: "short",
            year: "numeric",
          });
          return (
            <li key={occ.date}>
              <Link
                to={`/v/${venueSlug}/${eventSlug}?date=${occ.date}`}
                data-testid={`upcoming-date-${occ.date}`}
                className={`flex items-center justify-between gap-3 px-3 py-2.5 border transition-colors ${
                  isActive
                    ? "border-[#D9A6F5] bg-[#D9A6F5]/10"
                    : "border-white/10 hover:border-[#D9A6F5]/40"
                }`}
              >
                <div className="min-w-0">
                  <div className={`text-sm ${isActive ? "text-[#F5F5F0]" : "text-[#F5F5F0]"} font-bold truncate`}>
                    {label}
                  </div>
                  {displayTheme && (
                    <div
                      className={`text-[10px] uppercase tracking-[0.22em] truncate ${
                        isActive ? "text-[#D9A6F5]" : "text-[#A3A3A3]"
                      }`}
                    >
                      {displayTheme}
                    </div>
                  )}
                </div>
                <span className={isActive ? "text-[#D9A6F5]" : "text-[#A3A3A3]"}>→</span>
              </Link>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
